import { Trigger } from '@radix-ui/react-navigation-menu'
import { cn } from '@seen/ui'
import { IconChevronDown } from '@tabler/icons-react'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type MouseEventHandler,
  useRef,
} from 'react'

type TriggerProps = ComponentPropsWithoutRef<typeof Trigger>

export interface NavigationMenuTriggerProps extends TriggerProps {
  asChild?: TriggerProps['asChild']
  hideIcon?: boolean
  /**
   * Data attributes:
   *   [data-state]: "open" | "closed"
   *   [data-disabled]: Present when disabled
   */
  className?: TriggerProps['className']
}

export const NavigationMenuTrigger = forwardRef<
  ElementRef<typeof Trigger>,
  NavigationMenuTriggerProps
>(
  (
    {
      asChild,
      hideIcon,
      children,
      className,
      onMouseEnter,
      onClick,
      ...delegated
    },
    ref
  ) => {
    const mouseEnterTimeRef = useRef(0)

    const handleMouseEnter: MouseEventHandler<HTMLButtonElement> = (e) => {
      mouseEnterTimeRef.current = Date.now()
      onMouseEnter?.(e)
    }

    const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
      const clickWasTooQuick = Date.now() - mouseEnterTimeRef.current < 300
      if (clickWasTooQuick) {
        e.preventDefault()
      }
      onClick?.(e)
    }

    if (asChild) {
      return (
        <Trigger
          ref={ref}
          asChild
          className={className}
          onMouseEnter={handleMouseEnter}
          onClick={handleClick}
          {...delegated}
        >
          {children}
        </Trigger>
      )
    }
    return (
      <Trigger
        ref={ref}
        className={cn(
          'group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 font-medium text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50',
          className
        )}
        onMouseEnter={handleMouseEnter}
        onClick={handleClick}
        {...delegated}
      >
        {children}
        {!hideIcon && (
          <IconChevronDown
            className="relative top-px ml-2 size-3 transition duration-200 group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        )}
      </Trigger>
    )
  }
)
NavigationMenuTrigger.displayName = Trigger.displayName
